import { useEffect } from "react";
import { api } from "@/lib/api";
import { liveLog } from "@/lib/liveLog";
import { syncBreakingFromWindow } from "@/lib/breaking";

/**
 * Shared live poller (one instance per tab). Fetches /public/latest?scope=all
 * and dispatches window events:
 *   articles:new     → items never seen before (all sections)
 *   articles:update  → items whose updated_at changed
 *   articles:removed → ids that vanished from the visible list (withdrawn)
 *   articles:list    → news-section items (palestine/world/breaking) for ticker/widget
 *   articles:latest  → newest news-section item or null
 *   articles:status  → { ok, at, error }
 *
 * Guarantees: in-flight requests are coalesced, stale responses (lower seq)
 * are dropped, and refresh is triggered on visibility/online/focus/pageshow
 * and by refreshLiveArticles() (route changes). Back-off doubles on failure.
 */
const NEWS_SECTIONS = new Set(["palestine", "world", "breaking"]);
const state = { started: false, known: new Map(), seq: 0, applied: 0, inflight: null, timer: null, interval: 15000, current: 15000, primed: false };

const emit = (name, detail) => window.dispatchEvent(new CustomEvent(name, { detail }));

async function fetchLatest() {
    const seq = ++state.seq;
    const t0 = performance.now();
    try {
        const { data } = await api.get("/public/latest", { params: { limit: 15, scope: "all", _ts: Date.now() } });
        if (seq < state.applied) { liveLog("poll.stale-dropped", { seq }); return null; }
        state.applied = seq;
        const items = Array.isArray(data) ? data : [];
        const news = [], updates = [], seenNow = new Set();
        for (const a of items) {
            if (!a?.id) continue;
            seenNow.add(a.id);
            const stamp = a.updated_at || a.published_at || "";
            const pub = a.published_at || "";
            if (!state.known.has(a.id)) {
                if (state.primed) news.push(a);
                state.known.set(a.id, { stamp, pub });
            } else if (state.known.get(a.id).stamp !== stamp) {
                updates.push(a); state.known.set(a.id, { stamp, pub });
            }
        }
        // Withdrawn = known id missing although it is newer than the oldest item
        // still in the window (ids that simply aged out of the window are kept).
        const removed = [];
        const oldest = items.length ? (items[items.length - 1].published_at || "") : "";
        if (state.primed && items.length) {
            for (const [id, k] of state.known) if (!seenNow.has(id)) { if (k.pub >= oldest) removed.push(id); else state.known.delete(id); }
            for (const id of removed) state.known.delete(id);
        }
        state.primed = true;
        syncBreakingFromWindow(items);
        const newsOnly = items.filter((a) => NEWS_SECTIONS.has(a.section_slug));
        if (news.length) emit("articles:new", news);
        if (updates.length) emit("articles:update", updates);
        if (removed.length) emit("articles:removed", removed);
        emit("articles:latest", newsOnly[0] || null);
        emit("articles:list", newsOnly);
        emit("articles:status", { ok: true, at: Date.now() });
        state.current = state.interval;
        liveLog("poll.ok", { seq, n: items.length, new: news.length, upd: updates.length, rm: removed.length, ms: Math.round(performance.now() - t0) });
        return items;
    } catch (e) {
        state.current = Math.min(state.current * 2, 120_000);
        emit("articles:status", { ok: false, at: Date.now(), error: e?.response?.status || "network" });
        liveLog("poll.fail", { seq, status: e?.response?.status || "network" });
        return null;
    }
}

function schedule() {
    clearTimeout(state.timer);
    state.timer = setTimeout(tick, state.current);
}

async function tick() {
    // Hidden tab: no network work at all; `visibilitychange` wakes the poller and refreshes immediately on return.
    if (document.visibilityState === "hidden") { state.timer = null; return; }
    await refreshLiveArticles("timer");
    schedule();
}

export function refreshLiveArticles(reason = "manual") {
    if (state.inflight) return state.inflight;
    liveLog("poll.start", { reason });
    state.inflight = fetchLatest().finally(() => { state.inflight = null; });
    return state.inflight;
}

function start(interval) {
    if (state.started) return;
    state.started = true;
    state.interval = state.current = interval;
    const wake = (reason) => () => { if (document.visibilityState === "visible") { refreshLiveArticles(reason); schedule(); } };
    document.addEventListener("visibilitychange", wake("visibility"));
    window.addEventListener("online", wake("online"));
    window.addEventListener("focus", wake("focus"));
    window.addEventListener("pageshow", (e) => { if (e.persisted) wake("bfcache")(); });
    tick();
}

export function useLiveArticles(interval = 15000) {
    useEffect(() => { start(interval); }, [interval]);
}
